import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "../auth";
import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { waLink } from "../lib/wa";
import { Sparkles, Rocket, Palette, Globe, Bot, Shield, MessageCircle, ArrowRight, CheckCircle2, Zap, ShoppingBag, Briefcase, Newspaper, PenTool, Image as ImageIcon, Utensils, GraduationCap, Layers, Heart, User as UserIcon, Wrench, ExternalLink } from "lucide-react";

const TYPE_ICONS = { ecommerce: ShoppingBag, business: Briefcase, news: Newspaper, blog: PenTool, portfolio: ImageIcon, restaurant: Utensils, school: GraduationCap, agency: Layers, ngo: Heart, personal: UserIcon, service: Wrench, landing: Rocket };
const TYPE_ACCENTS = ["from-sky-500 to-cyan-500", "from-violet-500 to-fuchsia-500", "from-rose-500 to-orange-500", "from-emerald-500 to-teal-500", "from-amber-500 to-yellow-500", "from-red-500 to-pink-500", "from-blue-500 to-indigo-500", "from-purple-500 to-violet-500", "from-pink-500 to-rose-500", "from-teal-500 to-cyan-500", "from-slate-700 to-slate-900", "from-orange-500 to-red-500"];

export default function OxyLanding() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [oxy, setOxy] = useState(null);
  const [types, setTypes] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/oxy/settings").then(r => setOxy(r.data));
    api.get("/oxy/website-types").then(r => setTypes(r.data));
    api.get("/builders").then(r => setBuilders(r.data)).catch(() => {});
    api.get("/showcase").then(r => setShowcase(r.data)).catch(() => {});
    api.get("/plans").then(r => setPlans(r.data));
  }, []);

  const startBuild = (t) => {
    // Redirect to builder-specific route which handles auth/login
    const builder = builders.find(b => b.type === t || b.id === t);
    if (builder) { nav(builder.route); return; }
    if (!user) { sessionStorage.setItem("oxy_pending_type", t); login(); return; }
    nav(`/websites/new?type=${t}`);
  };

  useEffect(() => {
    if (user) {
      const t = sessionStorage.getItem("oxy_pending_type");
      if (t) { sessionStorage.removeItem("oxy_pending_type"); nav(`/websites/new?type=${t}`); }
    }
  }, [user]);

  const brandLogo = oxy?.platform_logo_url ? (
    <img src={fileUrl(oxy.platform_logo_url)} alt="OxyCom" className="h-9"/>
  ) : (
    <span className="text-2xl font-black tracking-tight flex items-center gap-2">
      <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-sky-500/40">
        <Sparkles className="w-5 h-5 text-white"/>
      </span>
      <span className="bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">OxyCom</span>
    </span>
  );

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,#e0f2fe_0%,transparent_60%),radial-gradient(ellipse_50%_40%_at_100%_20%,#f0fdfa_0%,transparent_60%),#ffffff]">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between relative z-10">
        <Link to="/">{brandLogo}</Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/affiliate/signup"><Button variant="ghost" size="sm" className="text-slate-700 hidden sm:inline-flex" data-testid="nav-affiliate">Affiliate</Button></Link>
          {user ? (
            <Link to="/dashboard"><Button size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="nav-dash">Dashboard</Button></Link>
          ) : (
            <Button size="sm" onClick={login} className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="nav-login">Sign in</Button>
          )}
        </div>
      </nav>

      {/* Hero + Brain Animation */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-slate-200 text-xs font-semibold text-slate-600">
              <Zap className="w-3.5 h-3.5 text-amber-500"/> OxyCom-powered · Bangladesh · Made for the world
            </div>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tight text-slate-900">
              Build any website <span className="bg-gradient-to-r from-sky-600 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">without code.</span>
            </h1>
            <p className="mt-6 font-bn text-xl text-slate-600 max-w-xl leading-relaxed">
              {oxy?.tagline_bn || "নিজের হাতে বানান পেশাদার ওয়েবসাইট"} — ই-কমার্স, নিউজ পোর্টাল, পোর্টফোলিও, রেস্টুরেন্ট বা যেকোনো ব্যবসার ওয়েবসাইট। AI সাহায্য করবে, আপনি Publish করবেন।
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => document.getElementById("types")?.scrollIntoView({behavior:"smooth"})} className="bg-slate-900 hover:bg-slate-800 rounded-full px-8 h-14 text-base font-bold shadow-xl shadow-slate-900/20" data-testid="hero-start">
                Choose a website type <ArrowRight className="w-5 h-5 ml-2"/>
              </Button>
              <a href="#showcase"><Button size="lg" variant="outline" className="rounded-full px-8 h-14 text-base font-bold border-2">See real projects</Button></a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500"/> ৳1,499 one-time build</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500"/> ৳499/মাস hosting</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500"/> Custom domain + SSL</span>
            </div>
          </div>
          <BrainAnimation/>
        </div>
      </section>

      {/* Website Types Grid */}
      <section id="types" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-14">
          <div className="inline-block px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-bold uppercase tracking-wider">Choose your website type</div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black text-slate-900">Twelve powerful builders. <span className="text-slate-400">One platform.</span></h2>
          <p className="mt-3 font-bn text-slate-600">যেটা দরকার তার নিচের <b>Create Now</b> ক্লিক করুন — শুধু সেই বিল্ডার-ই খুলবে।</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {types.map((t, i) => {
            const Icon = TYPE_ICONS[t.id] || Rocket;
            const accent = TYPE_ACCENTS[i % TYPE_ACCENTS.length];
            return (
              <div key={t.id} className="group relative rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-2xl hover:-translate-y-1 hover:border-slate-300 transition-all" data-testid={`type-card-${t.id}`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-6 h-6 text-white"/>
                </div>
                <div className="mt-4">
                  <div className="font-display text-xl font-bold text-slate-900">{t.name}</div>
                  <div className="font-bn text-sm text-slate-500 mt-0.5">{t.name_bn}</div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">{t.desc}</p>
                </div>
                <div className="mt-4 flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Build</div>
                    <div className="font-black font-mono text-slate-900">৳1,499</div>
                  </div>
                  <div className="w-px h-8 bg-slate-200"/>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-500 font-bold">Demo only</div>
                    <div className="font-black font-mono text-emerald-600">৳19</div>
                  </div>
                </div>
                <Button onClick={() => startBuild(t.id)} className={`mt-4 w-full bg-gradient-to-r ${accent} text-white font-bold rounded-lg h-11 shadow-md hover:shadow-xl transition-all`} data-testid={`create-${t.id}`}>
                  Create Now <ArrowRight className="w-4 h-4 ml-1"/>
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Showcase — Successful Projects */}
      <section id="showcase" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider">Live on OxyCom</div>
            <h2 className="mt-3 font-display text-4xl font-black text-slate-900">Real projects. Real clients.</h2>
            <p className="mt-1 font-bn text-slate-600">আমাদের বানানো কিছু সফল ওয়েবসাইট।</p>
          </div>
        </div>
        {showcase.length === 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {title: "Rashida Boutique", category: "E-commerce", desc: "Handloom saree store — 3× sales in 60 days.", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800"},
              {title: "Dhaka Daily", category: "News Portal", desc: "Bengali news portal with e-paper.", img: "https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800"},
              {title: "Rahim Studio", category: "Portfolio", desc: "Photographer showcase — bookings up 4×.", img: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800"},
            ].map((s, i) => (
              <div key={i} className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover:shadow-2xl transition-all" data-testid={`showcase-demo-${i}`}>
                <div className="aspect-video bg-cover bg-center" style={{backgroundImage: `url(${s.img})`}}/>
                <div className="p-5">
                  <div className="text-xs font-bold text-sky-600 uppercase">{s.category}</div>
                  <div className="font-display text-lg font-bold mt-1">{s.title}</div>
                  <div className="text-sm text-slate-500 mt-1">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {showcase.map(s => (
              <a key={s.showcase_id} href={s.url || "#"} target="_blank" rel="noreferrer" className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all" data-testid={`showcase-${s.showcase_id}`}>
                <div className="aspect-video bg-cover bg-center bg-slate-100" style={{backgroundImage: s.image_url ? `url(${fileUrl(s.image_url)})` : "none"}}/>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-sky-600 uppercase">{s.category}</div>
                    {s.featured && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Featured</span>}
                  </div>
                  <div className="font-display text-lg font-bold mt-1 flex items-center gap-1">{s.title} <ExternalLink className="w-3 h-3 opacity-40 group-hover:opacity-100"/></div>
                  <div className="text-sm text-slate-500 mt-1 line-clamp-2">{s.description}</div>
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-bold uppercase tracking-wider">Pricing</div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black text-slate-900">Simple, honest pricing.</h2>
            <p className="mt-3 font-bn text-slate-600">একবার বিল্ড ফি, তারপর শুধু হোস্টিং।</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p, i) => (
              <div key={p.plan_id} className={`rounded-2xl p-7 border-2 ${p.featured ? "bg-gradient-to-br from-slate-900 to-slate-800 text-white border-slate-900 shadow-2xl scale-105" : "bg-white border-slate-200"}`} data-testid={`plan-${p.plan_id}`}>
                {p.featured && <div className="text-[10px] font-bold bg-amber-400 text-slate-900 px-2 py-1 rounded-full inline-block mb-3">MOST POPULAR</div>}
                <div className="font-bn font-bold text-sm opacity-75">{p.name_bn}</div>
                <div className="font-display text-2xl font-black mt-1">{p.name}</div>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-5xl font-black">৳{p.price_bdt.toLocaleString("bn-BD")}</span>
                  {p.billing !== "one_time" && <span className={`text-sm ${p.featured ? "opacity-70" : "text-slate-500"}`}>/{p.billing === "monthly" ? "mo" : "yr"}</span>}
                </div>
                <div className={`text-xs mt-1 ${p.featured ? "opacity-60" : "text-slate-500"}`}>≈ ${p.price_usd} USD</div>
                <div className="mt-6 space-y-2">
                  {(p.features || []).map((f, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm font-bn">
                      <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${p.featured ? "text-emerald-400" : "text-emerald-500"}`}/>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => user ? nav("/dashboard") : login()} className={`w-full mt-6 h-12 font-bold rounded-full ${p.featured ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"}`} data-testid={`plan-cta-${p.plan_id}`}>
                  Get started
                </Button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500 mt-6">আরও প্যাকেজ শীঘ্রই আসছে — Admin সেট করতে পারবেন।</p>
        </div>
      </section>

      {/* Affiliate CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 p-10 sm:p-14 text-white relative">
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: "radial-gradient(circle at 30% 20%, white 0, transparent 40%), radial-gradient(circle at 80% 70%, white 0, transparent 40%)"}}/>
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest opacity-80">Earn with OxyCom</div>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl font-black">Become an affiliate. Earn on every sale.</h2>
            <p className="mt-3 font-bn max-w-2xl text-white/90 text-lg">আমাদের সাথে যুক্ত হোন — প্রতিটি প্রোডাক্টের জন্য পাবেন ইউনিক লিংক ও কমিশন।</p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/affiliate/signup"><Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 h-14 font-bold" data-testid="cta-affiliate-signup">Sign up as affiliate</Button></Link>
              <Link to="/affiliate/login"><Button size="lg" variant="outline" className="border-white/50 border-2 text-white bg-white/10 hover:bg-white/20 rounded-full px-8 h-14 font-bold">I already have an account</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Founder & CEO</div>
        <div className="mt-4 w-24 h-24 rounded-full mx-auto bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center text-white text-3xl font-black shadow-xl">
          {(oxy?.founder_name || "R H J").split(" ").slice(0,2).map(x=>x[0]).join("")}
        </div>
        <div className="mt-4 font-display text-2xl font-black">{oxy?.founder_name || "Robiul H Jihad"}</div>
        <div className="text-sm text-slate-500">{oxy?.founder_designation || "Founder & CEO"}</div>
        <p className="font-bn mt-4 text-slate-600 max-w-2xl mx-auto">{oxy?.founder_bio}</p>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-sm text-slate-500 flex flex-wrap gap-4 justify-between items-center">
          <div>© 2026 {oxy?.brand_name || "OxyCom"} · Made in Bangladesh</div>
          <div className="flex gap-4">
            <Link to="/affiliate/signup" className="hover:text-slate-900">Affiliate</Link>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href={waLink(oxy?.whatsapp_number)} target="_blank" rel="noreferrer" className="hover:text-slate-900">WhatsApp</a>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={waLink(oxy?.whatsapp_number, "আমি OxyCom থেকে ওয়েবসাইট বানাতে চাই")} target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-2xl shadow-emerald-500/40 flex items-center justify-center transition-transform hover:scale-110 z-40" data-testid="whatsapp-fab" aria-label="WhatsApp">
        <MessageCircle className="w-6 h-6"/>
      </a>
    </div>
  );
}

/* ─────────────────── AI Brain Motion Graphic ─────────────────── */
function BrainAnimation() {
  return (
    <div className="relative aspect-square max-w-lg mx-auto">
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-200/40 via-cyan-200/30 to-emerald-200/40 blur-3xl"/>
      <svg viewBox="0 0 400 400" className="w-full h-full relative">
        <defs>
          <radialGradient id="core" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="1"/>
            <stop offset="60%" stopColor="#06b6d4" stopOpacity="0.7"/>
            <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
          </radialGradient>
          <linearGradient id="line" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0ea5e9"/><stop offset="100%" stopColor="#10b981"/>
          </linearGradient>
        </defs>

        {/* Orbits */}
        <g className="brain-orbit-slow" style={{transformOrigin:"200px 200px"}}>
          <ellipse cx="200" cy="200" rx="170" ry="60" fill="none" stroke="url(#line)" strokeWidth="1" strokeDasharray="4 6" opacity="0.4"/>
        </g>
        <g className="brain-orbit-mid" style={{transformOrigin:"200px 200px"}}>
          <ellipse cx="200" cy="200" rx="140" ry="140" fill="none" stroke="url(#line)" strokeWidth="1.2" strokeDasharray="2 8" opacity="0.5"/>
        </g>
        <g className="brain-orbit-fast" style={{transformOrigin:"200px 200px"}}>
          <ellipse cx="200" cy="200" rx="60" ry="170" fill="none" stroke="url(#line)" strokeWidth="1" strokeDasharray="4 6" opacity="0.4"/>
        </g>

        {/* Neural network */}
        {NEURAL_NODES.map((n, i) => (
          <circle key={i} cx={n.x} cy={n.y} r={n.r} fill="#0ea5e9" opacity="0.9" className="brain-node" style={{animationDelay: `${i * 0.15}s`}}/>
        ))}
        {NEURAL_LINKS.map((l, i) => (
          <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="url(#line)" strokeWidth="1.4" opacity="0.35" className="brain-link" style={{animationDelay: `${i * 0.2}s`}}/>
        ))}

        {/* Core */}
        <circle cx="200" cy="200" r="120" fill="url(#core)" className="brain-pulse"/>
        <circle cx="200" cy="200" r="55" fill="#0f172a" opacity="0.85"/>
        <text x="200" y="196" textAnchor="middle" dominantBaseline="middle" fill="white" fontWeight="900" fontSize="18" fontFamily="Plus Jakarta Sans, sans-serif">OxyCom</text>
        <text x="200" y="222" textAnchor="middle" fill="#67e8f9" fontSize="9" fontWeight="700" letterSpacing="3">POWERED</text>
      </svg>
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/> Live</span>
        <span>·</span>
        <span>Neural</span>
        <span>·</span>
        <span>Real-time</span>
      </div>
    </div>
  );
}

const NEURAL_NODES = [
  {x:80,y:120,r:5},{x:110,y:200,r:6},{x:80,y:280,r:5},
  {x:200,y:80,r:5},{x:200,y:320,r:6},
  {x:320,y:120,r:6},{x:290,y:200,r:5},{x:320,y:280,r:5},
  {x:150,y:150,r:4},{x:150,y:250,r:4},{x:250,y:150,r:4},{x:250,y:250,r:4},
];
const NEURAL_LINKS = [
  {x1:80,y1:120,x2:200,y2:80},{x1:80,y1:120,x2:110,y2:200},{x1:110,y1:200,x2:80,y2:280},
  {x1:80,y1:280,x2:200,y2:320},{x1:200,y1:80,x2:320,y2:120},{x1:320,y1:120,x2:290,y2:200},
  {x1:290,y1:200,x2:320,y2:280},{x1:320,y1:280,x2:200,y2:320},
  {x1:150,y1:150,x2:250,y2:150},{x1:150,y1:250,x2:250,y2:250},
  {x1:150,y1:150,x2:150,y2:250},{x1:250,y1:150,x2:250,y2:250},
  {x1:110,y1:200,x2:150,y2:150},{x1:110,y1:200,x2:150,y2:250},
  {x1:290,y1:200,x2:250,y2:150},{x1:290,y1:200,x2:250,y2:250},
];
